import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { formatPeso, type ReceivableStatus } from '@tahi/core';
import {
  addReceivable,
  deleteReceivable,
  editReceivable,
  receiveReceivable,
  restoreReceipt,
  restoreReceivable,
  undoReceipt,
} from '../../../data/commands/budget';
import { useCommandEnv } from '../../../data/hooks';
import { Button, ScreenHeader, Sheet, showToast } from '../../../ui';
import { TextField } from '../../../ui/TextField';
import { useGoBack } from '../../spending/hooks';
import { AccountChips } from '../components/AccountChips';
import { copy, errorText } from '../copy';
import { receivablesOf, type BudgetSnapshot } from '../derive';
import { dayShort, minorToInput, parsePesoInput } from '../format';
import { useBudgetSettings, useBudgetToday, useToday } from '../hooks';
import { ACCENT_LINK, CARD, QUIET_LINK, SCREEN } from '../styles';

const BACK = '/m/budget/owed';
const OWED_BACK = { to: '/m/budget', label: copy.moduleName };

/** v0.6 "Owed to you": open ones with what's left, receipts with undo, then the paid back ones. */
export function OwedScreen() {
  const s = useBudgetToday();
  const env = useCommandEnv();
  if (!s) {
    return (
      <main className={SCREEN}>
        <ScreenHeader title={copy.owedToYou} back={OWED_BACK} />
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }
  const all = receivablesOf(s);
  const open = all.filter((r) => !r.settled);
  const settled = all.filter((r) => r.settled);
  const total = open.reduce((sum, r) => sum + r.remainingMinor, 0);

  async function undo(receiptId: string) {
    const r = await undoReceipt(env, receiptId);
    if (r.ok) {
      showToast({
        message: copy.deletedMovement,
        action: { label: copy.undo, onPress: () => void restoreReceipt(env, receiptId) },
      });
    }
  }

  return (
    <main className={SCREEN}>
      <ScreenHeader
        title={copy.owedToYou}
        back={OWED_BACK}
        actions={
          <Link to="/m/budget/owed/new" className={ACCENT_LINK}>
            {copy.addReceivable}
          </Link>
        }
      />
      <p className="text-sm text-muted">
        {open.length > 0 ? (
          <span data-testid="owed-screen-total">{copy.owedTotal(total)} · </span>
        ) : null}
        {copy.owedNote}
      </p>
      {open.length === 0 ? <p className="text-muted">{copy.nothingOwed}</p> : null}
      {open.map((r) => (
        <ReceivableCard key={r.receivable.id} r={r} s={s} onUndo={(id) => void undo(id)} />
      ))}
      {settled.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="text-[20px] font-bold text-fg">{copy.settled}</h2>
          {settled.map((r) => (
            <ReceivableCard key={r.receivable.id} r={r} s={s} onUndo={(id) => void undo(id)} />
          ))}
        </section>
      ) : null}
    </main>
  );
}

function ReceivableCard({
  r,
  s,
  onUndo,
}: {
  r: ReceivableStatus;
  s: BudgetSnapshot;
  onUndo(receiptId: string): void;
}) {
  const accountName = (id: string | null) =>
    (id && s.accounts.find((a) => a.id === id)?.name) || null;
  const { receivable: item } = r;
  return (
    <section
      aria-label={item.name}
      data-testid={`receivable-${item.name}`}
      className={`${CARD} flex flex-col gap-2`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="truncate text-lg font-bold text-fg">{item.name}</h3>
          <p className="text-sm text-muted">
            {r.settled ? copy.paidBack : copy.owedLeft(r.remainingMinor, item.amountMinor)}
            {item.expectedDate && !r.settled ? ` · ${dayShort(item.expectedDate)}` : ''}
          </p>
          {item.note ? <p className="text-sm text-muted">{item.note}</p> : null}
        </div>
        <span
          data-testid="receivable-left"
          className="shrink-0 font-mono text-lg tabular-nums text-fg"
        >
          {formatPeso(r.remainingMinor, { cents: 'auto' })}
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {!r.settled ? (
          <Link to={`/m/budget/owed/${item.id}/receive`} className={ACCENT_LINK}>
            {copy.receive}
          </Link>
        ) : null}
        <Link to={`/m/budget/owed/${item.id}/edit`} className={QUIET_LINK}>
          {copy.editReceivable}
        </Link>
      </div>
      {r.receipts.length > 0 ? (
        <div className="flex flex-col gap-1">
          <h4 className="text-sm font-bold text-muted">{copy.receipts}</h4>
          <ul aria-label={`${copy.receipts}: ${item.name}`} className="divide-y divide-divider">
            {r.receipts.map((e) => {
              const into = accountName(e.toAccountId);
              return (
                <li key={e.id} className="flex min-h-11 items-center justify-between gap-3">
                  <span className="min-w-0 text-sm text-fg">
                    {dayShort(e.date)}
                    {into ? <span className="text-muted"> · {into}</span> : null}
                  </span>
                  <span className="flex shrink-0 items-center gap-1">
                    <span className="font-mono tabular-nums text-success">
                      +{formatPeso(e.amountMinor, { cents: 'auto' })}
                    </span>
                    <button
                      type="button"
                      aria-label={`${copy.undoReceipt}: ${formatPeso(e.amountMinor, { cents: 'auto' })} from ${item.name}`}
                      onClick={() => onUndo(e.id)}
                      className={`${QUIET_LINK} px-2`}
                    >
                      {copy.undoReceipt}
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

/** Add or edit who owes you what. */
export function ReceivableFormRoute() {
  const { id } = useParams();
  const s = useBudgetToday();
  const goBack = useGoBack(BACK);
  if (!s) return null;
  const item = id ? receivablesOf(s).find((r) => r.receivable.id === id) : undefined;
  if (id && !item) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.editReceivableTitle}>
        <p className="text-muted">{errorText('not_found')}</p>
      </Sheet>
    );
  }
  return <ReceivableForm item={item} />;
}

function ReceivableForm({ item }: { item?: ReceivableStatus }) {
  const env = useCommandEnv();
  const navigate = useNavigate();
  const goBack = useGoBack(BACK);
  const r = item?.receivable;
  const [name, setName] = useState(r?.name ?? '');
  const [amount, setAmount] = useState(r ? minorToInput(r.amountMinor) : '');
  const [date, setDate] = useState(r?.expectedDate ?? '');
  const [note, setNote] = useState(r?.note ?? '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [armed, setArmed] = useState(false);
  const minor = parsePesoInput(amount);

  async function save() {
    if (name.trim() === '') return setError(errorText('name_required'));
    if (minor === null) return setError(errorText('bad_amount'));
    setBusy(true);
    const input = { name, amountMinor: minor, expectedDate: date || null, note: note || null };
    const res = r ? await editReceivable(env, r.id, input) : await addReceivable(env, input);
    setBusy(false);
    if (!res.ok) return setError(errorText(res.error));
    goBack();
  }

  async function remove() {
    if (!r) return;
    if (!armed) return setArmed(true);
    const res = await deleteReceivable(env, r.id);
    if (res.ok) {
      const receiptIds = res.receiptIds;
      showToast({
        message: copy.deletedReceivable(r.name),
        action: {
          label: copy.undo,
          onPress: () => void restoreReceivable(env, r.id, receiptIds),
        },
      });
    }
    navigate(BACK, { replace: true });
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={r ? copy.editReceivableTitle : copy.addReceivableTitle}
      description={copy.owedNote}
      footer={
        <Button variant="primary" block disabled={busy} onClick={() => void save()}>
          {copy.save}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label={copy.whoOwes}
          placeholder={copy.whoOwesPlaceholder}
          value={name}
          maxLength={60}
          required
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          label={copy.owedAmount}
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          label={copy.expectedOnOptional}
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
        <TextField
          label={copy.noteOptional}
          value={note}
          maxLength={200}
          onChange={(e) => setNote(e.target.value)}
        />
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
        {r ? (
          <Button variant="destructive" block onClick={() => void remove()}>
            {armed ? copy.confirmDelete : copy.delete}
          </Button>
        ) : null}
      </div>
    </Sheet>
  );
}

/** "Received" part or all of it, into an account: a deposit, never income. */
export function ReceiveReceivableRoute() {
  const { id = '' } = useParams();
  const s = useBudgetToday();
  const goBack = useGoBack(BACK);
  if (!s) return null;
  const item = receivablesOf(s).find((r) => r.receivable.id === id);
  if (!item || item.settled) {
    return (
      <Sheet open onOpenChange={(open) => !open && goBack()} title={copy.receive}>
        <p className="text-muted">{item ? copy.paidBack : errorText('not_found')}</p>
      </Sheet>
    );
  }
  return <ReceiveSheet item={item} s={s} />;
}

function ReceiveSheet({ item, s }: { item: ReceivableStatus; s: BudgetSnapshot }) {
  const env = useCommandEnv();
  const today = useToday();
  const settings = useBudgetSettings();
  const goBack = useGoBack(BACK);
  const accounts = s.accounts;
  const [amount, setAmount] = useState(minorToInput(item.remainingMinor));
  const [date, setDate] = useState(today);
  const [accountId, setAccountId] = useState<string | null>(
    settings?.incomeAccountId ?? accounts[0]?.id ?? null,
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const minor = parsePesoInput(amount);

  async function save() {
    if (minor === null) return setError(errorText('bad_amount'));
    setBusy(true);
    const r = await receiveReceivable(env, item.receivable.id, {
      amountMinor: minor,
      date,
      accountId,
    });
    setBusy(false);
    if (!r.ok) return setError(errorText(r.error));
    const receiptId = r.receiptId;
    showToast({
      message: copy.receivedReceivable(
        minor,
        accounts.find((a) => a.id === accountId)?.name ?? null,
      ),
      action: { label: copy.undo, onPress: () => void undoReceipt(env, receiptId) },
    });
    goBack();
  }

  return (
    <Sheet
      open
      onOpenChange={(open) => !open && goBack()}
      title={copy.receiveTitle(item.receivable.name)}
      footer={
        <Button
          variant="primary"
          block
          disabled={minor === null || busy}
          onClick={() => void save()}
        >
          {copy.receive}
        </Button>
      }
    >
      <div className="flex flex-col gap-4">
        <TextField
          label={copy.amount}
          inputMode="decimal"
          value={amount}
          hint={copy.receiveHint(item.remainingMinor)}
          onChange={(e) => setAmount(e.target.value)}
        />
        <TextField
          label={copy.receivedOn}
          type="date"
          value={date}
          max={today}
          onChange={(e) => e.target.value && setDate(e.target.value)}
        />
        {accounts.length > 0 ? (
          <AccountChips
            label={copy.landsIn}
            accounts={accounts}
            value={accountId}
            onChange={setAccountId}
            clearable={false}
          />
        ) : null}
        <p className="text-sm text-muted">{copy.owedNote}</p>
        {error ? (
          <p role="alert" className="text-sm text-destructive">
            {error}
          </p>
        ) : null}
      </div>
    </Sheet>
  );
}
